'use client';
import React from 'react';
/**
 * Shared by the date and time boxes: the box shows the value, a click opens our own popup
 * (a portal, so a modal's scroll area does not cut it off) and a pick goes back through a
 * hidden carrier input of the matching type.
 */
/** Marks a read-only date / time box that is still a form field (Enter-to-next stops on it) */
export const PICKER_BOX_ATTR = 'data-picker-box';
/** The visible box of a picker: the other input inside the same `relative` wrapper as the carrier */
export function pickerBox(carrier) {
    return carrier?.closest('.relative')?.querySelector('input:not([data-picker-carrier])') ?? null;
}
/**
 * Puts a value into the carrier input as a real change event, so the form's
 * "Discard changes?" tracking (onChangeCapture) sees a pick like any typed field.
 */
export function sendThroughCarrier(carrier, value, fallback) {
    if (!carrier)
        return fallback(value);
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(carrier, value);
    carrier.dispatchEvent(new Event('input', { bubbles: true }));
}
/**
 * Places an open popup under its box, from the box's left. No room below: scrolls the page /
 * modal body up so it fits; only when that cannot make room does it open above the box.
 * Closes on a mouse press outside the box and the popup.
 */
export function usePopupBelow({ open, setOpen, getBox, popupRef, width, }) {
    const [position, setPosition] = React.useState(null);
    React.useLayoutEffect(() => {
        if (!open)
            return;
        const height = () => popupRef.current?.offsetHeight || 360;
        const place = () => {
            const rect = getBox()?.getBoundingClientRect();
            if (!rect)
                return;
            const w = width(rect.width);
            const below = window.innerHeight - rect.bottom >= height() + 12 || rect.top < height() + 12;
            const next = {
                top: below ? rect.bottom + 4 : rect.top - 4 - height(),
                left: Math.max(8, Math.min(rect.left, window.innerWidth - w - 8)),
                width: w,
            };
            // Scrolling a list inside the popup also fires 'scroll': re-render only when the spot moved
            setPosition((p) => (p && p.top === next.top && p.left === next.left && p.width === next.width ? p : next));
        };
        const rect = getBox()?.getBoundingClientRect();
        const short = rect ? rect.bottom + 4 + height() + 12 - window.innerHeight : 0;
        if (rect && short > 0) {
            let scroller = getBox()?.parentElement ?? null;
            while (scroller && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) {
                scroller = scroller.parentElement;
            }
            // Never push the box itself above the top of the window
            (scroller ?? document.scrollingElement)?.scrollBy({ top: Math.min(short, rect.top - 8) });
        }
        place();
        const close = (e) => {
            const target = e.target;
            if (popupRef.current?.contains(target) || getBox()?.parentElement?.contains(target))
                return;
            setOpen(false);
        };
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        document.addEventListener('mousedown', close);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
            document.removeEventListener('mousedown', close);
        };
    }, [open]);
    /** Style for the popup: hidden until placed, so its real height is known when placing it */
    return position ?? { top: 0, left: 0, width: width(0), visibility: 'hidden' };
}
/**
 * Scrolls each list in the popup (a `relative` box) so its chosen item ([data-selected]) is in view — only when it
 * is out of view, and only when called (popup opened / value changed), never while the user scrolls.
 */
export function showSelected(popup) {
    popup?.querySelectorAll('[data-selected]').forEach((el) => {
        const list = el.parentElement;
        if (!list || list.scrollHeight <= list.clientHeight)
            return;
        const top = el.offsetTop; // the list is position: relative, so this is from the list's top
        if (top < list.scrollTop || top + el.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = top - list.clientHeight / 2 + el.offsetHeight / 2;
        }
    });
}
